import type { EditorialWriteStatus } from "../types";

/**
 * Permission required to move an editorial content row to a given target review
 * status. `pending` and `ready_for_editing` are the researcher's
 * `staff.research.change_status` power; `ready_for_translation` and
 * `ready_for_release` are the editor's `staff.research.approve` power.
 *
 * This is the single source of truth on the frontend and MUST stay in lockstep
 * with research-api's `src/utils/reviewStatusPermission.ts`. The backend gates
 * the editorial-content PATCH by target status against exactly this map, so any
 * drift here re-introduces the 403 this gate exists to spare the user.
 */
const REQUIRED_PERMISSION_BY_REVIEW_STATUS: Record<EditorialWriteStatus, string> = {
  pending: "staff.research.change_status",
  ready_for_editing: "staff.research.change_status",
  ready_for_translation: "staff.research.approve",
  ready_for_release: "staff.research.approve",
};

export function requiredPermissionForReviewStatus(status: EditorialWriteStatus): string {
  return REQUIRED_PERMISSION_BY_REVIEW_STATUS[status];
}

/**
 * True when the actor may set a row to this target review status. Delegates to
 * the app's `hasPermission`, which already honours the `*` wildcard — mirroring
 * the backend's `perms.includes("*") || perms.includes(required)` check without
 * re-implementing the wildcard rule here.
 */
export function canSetReviewStatus(
  hasPermission: (permission: string) => boolean,
  status: EditorialWriteStatus,
): boolean {
  return hasPermission(requiredPermissionForReviewStatus(status));
}
